import React, { useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Share2,
  Printer,
  Edit2,
  Trash2,
  Phone,
  MessageSquare,
  Send,
  Bot,
  Users,
  RefreshCw,
  ImagePlus,
  X,
  AlertCircle,
  Check,
  CheckCircle,
  Clock,
  Box,
  Truck,
  Home,
  PenLine,
  Globe,
} from "lucide-react";
import { formatCurrency } from "../utils/formatCurrency";
import { isOnlineOrder, onlineOrderLabel } from "../utils/orderSource";
import { ITEM_TERMINAL_STATUSES } from "../utils/statusConstants";
import SurveyFeedbackDisplay from "./SurveyFeedbackDisplay";

// ============================================================
// ReceiptDetailMobile — the phone view of one receipt.
//
// Design rules (iOS HIG / Material full-screen detail):
//   - one nav bar: Back, title, a single "more" menu
//   - a hero that answers "where is this order" in one glance
//   - items are the middle of the screen; ONE status control per row
//   - everything else is a grouped list, label left / value right
//   - one primary action fixed at the bottom
//   - every tap target >= 44pt, no tinted row backgrounds, no emoji
//
// All data and every write go through the existing dashboard hook; this
// file only renders. Nothing here talks to Supabase directly.
// ============================================================

const STATUS_LABEL = {
  waiting: "Waiting",
  ready: "Ready",
  partial: "Partly ready",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  picked_up: "Picked up",
};

const STATUS_DOT = {
  waiting: "bg-slate-300",
  ready: "bg-teal-500",
  partial: "bg-teal-500",
  out_for_delivery: "bg-sky-500",
  delivered: "bg-emerald-500",
  picked_up: "bg-emerald-500",
};

const STATUS_CHIP = {
  waiting: "text-slate-600 bg-slate-100",
  ready: "text-teal-800 bg-teal-50",
  partial: "text-teal-800 bg-teal-50",
  out_for_delivery: "text-sky-800 bg-sky-50",
  delivered: "text-emerald-800 bg-emerald-50",
  picked_up: "text-emerald-800 bg-emerald-50",
};

const STATUS_ICON = {
  waiting: Clock,
  ready: Box,
  partial: Box,
  out_for_delivery: Truck,
  delivered: Home,
  picked_up: CheckCircle,
};

// Plain "YYYY-MM-DD" must be built as a local day; new Date("YYYY-MM-DD")
// is UTC midnight and shows the evening before in US timezones.
const parseDateOnly = (value) => {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDay = (value, opts = { weekday: "short", month: "short", day: "numeric" }) => {
  const d = parseDateOnly(value);
  return d ? d.toLocaleDateString("en-US", opts) : null;
};

const money = (n) => `$${formatCurrency(parseFloat(n) || 0)}`;

const digitsOnly = (p) => String(p || "").replace(/\D/g, "");

// ------------------------------------------------------------
// Small building blocks
// ------------------------------------------------------------

// Bottom action sheet. Modal, slides up, closes on backdrop tap.
const Sheet = ({ open, title, subtitle, onClose, children }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[130] flex items-end bg-slate-900/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full bg-white rounded-t-2xl shadow-2xl rdm-sheet-in">
        <div className="pt-2 pb-1 flex justify-center">
          <div className="w-9 h-1 rounded-full bg-slate-300" />
        </div>
        {(title || subtitle) && (
          <div className="px-5 pt-1 pb-2">
            {title && (
              <p className="text-[15px] font-semibold text-slate-900 truncate">{title}</p>
            )}
            {subtitle && <p className="text-[13px] text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
        )}
        <div className="px-3 pb-3" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}>
          {children}
        </div>
      </div>
    </div>
  );
};

const SheetButton = ({ label, Icon, onClick, disabled, danger, current, busy }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || busy}
    className={`w-full min-h-[52px] px-4 flex items-center gap-3 rounded-xl text-left text-[16px] transition-colors active:bg-slate-100 disabled:opacity-40 ${
      danger ? "text-red-600" : "text-slate-900"
    } ${current ? "bg-slate-50" : ""}`}
  >
    {busy ? (
      <RefreshCw className="w-5 h-5 animate-spin text-slate-400 flex-shrink-0" />
    ) : Icon ? (
      <Icon className={`w-5 h-5 flex-shrink-0 ${danger ? "text-red-500" : "text-slate-500"}`} />
    ) : (
      <span className="w-5" />
    )}
    <span className="flex-1 font-medium">{label}</span>
    {current && <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />}
  </button>
);

// Grouped list: a section header and rows with a hairline between them.
const Group = ({ title, children, trailing }) => (
  <section className="mt-6">
    {(title || trailing) && (
      <div className="px-4 pb-2 flex items-end justify-between">
        <h3 className="text-[13px] font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
        {trailing}
      </div>
    )}
    <div className="mx-4 rounded-2xl bg-white border border-slate-200 overflow-hidden divide-y divide-slate-100">
      {children}
    </div>
  </section>
);

const Row = ({ label, value, sub, onClick, chevron, danger, Icon, valueClass = "" }) => {
  const inner = (
    <>
      {Icon && <Icon className="w-5 h-5 text-slate-400 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-[15px] text-slate-900 truncate">{label}</p>
        {sub && <p className="text-[13px] text-slate-500 truncate">{sub}</p>}
      </div>
      {value !== undefined && value !== null && (
        <p className={`text-[15px] text-right ${danger ? "text-red-600" : "text-slate-600"} ${valueClass}`}>
          {value}
        </p>
      )}
      {chevron && <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />}
    </>
  );
  const cls = "w-full min-h-[48px] px-4 py-3 flex items-center gap-3 text-left";
  return onClick ? (
    <button type="button" onClick={onClick} className={`${cls} active:bg-slate-50`}>
      {inner}
    </button>
  ) : (
    <div className={cls}>{inner}</div>
  );
};

// ------------------------------------------------------------
// Main component
// ------------------------------------------------------------
const ReceiptDetailMobile = ({ dashboard, renderMessageContent, formatMessageTime }) => {
  const {
    selectedReceipt,
    isLoadingReceiptDetails,
    handleAttemptCloseReceiptModal,
    handleCloseReceiptModal,
    handleShareReceipt,
    handlePrintReceipt,
    handleOpenModal,
    handleDeleteReceipt,
    handleSendSMS,
    sendingSMSReceiptId,
    smsSentReceipts,
    getAllItemStatusesForReceipt,
    allInventoryUnits,
    manufacturers,
    itemStatusBusy,
    markingItemReady,
    isUndoingDelivery,
    handleMarkItemReady,
    handleMarkUnitStatus,
    handleUndoReady,
    handleUndoDelivery,
    selectedItemsForDelivery,
    toggleItemForDelivery,
    clearSelectedItemsForDelivery,
    getPaymentStatus,
    storeSettings,
    openSignaturesDrawer,
    isMobileChatOpen,
    setIsMobileChatOpen,
    isStaffMode,
    handleTakeOverChat,
    handleReturnToAI,
    isTakingOver,
    isReturningToAI,
    staffMessage,
    setStaffMessage,
    staffImage,
    setStaffImage,
    sendingMessage,
    handleSendStaffMessage,
    mobileChatContainerRef,
    handleChatScroll,
    addNotification,
    escalations,
  } = dashboard;

  const [menuOpen, setMenuOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null); // item whose sheet is open
  const [selectMode, setSelectMode] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showAllPayments, setShowAllPayments] = useState(false);

  const r = selectedReceipt;
  const items = r?.items || [];

  // Per-item status, same derivation the desktop drawer uses.
  const statusMap = useMemo(
    () =>
      getAllItemStatusesForReceipt(
        items,
        r?.inventoryUnits,
        allInventoryUnits,
        r?.itemStatuses || r?.item_statuses || [],
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, r?.inventoryUnits, allInventoryUnits, r?.itemStatuses, r?.item_statuses],
  );

  const statusOf = (item) => statusMap.get(item.id) || { status: "waiting", delivered: 0, ready: 0, waiting: item.quantity || 1, outForDelivery: 0 };

  // Order-level readiness, mirrors the customer page so both screens agree.
  const readiness = useMemo(() => {
    const c = { received: 0, out: 0, ready: 0, waiting: 0, total: 0 };
    items.forEach((item) => {
      const qty = item.quantity || 1;
      const s = statusOf(item);
      c.total += qty;
      if (s.status === "delivered" || s.status === "picked_up") c.received += qty;
      else if (s.status === "out_for_delivery") c.out += qty;
      else if (s.status === "ready") c.ready += qty;
      else if (s.status === "partial") {
        const rd = Math.min(qty, s.ready || 0);
        c.ready += rd;
        c.waiting += qty - rd;
      } else c.waiting += qty;
    });
    const remaining = c.total - c.received;
    return {
      ...c,
      allDone: c.total > 0 && remaining <= 0,
      allOut: remaining > 0 && c.waiting === 0 && c.ready === 0 && c.out >= remaining,
      allReady: remaining > 0 && c.waiting === 0 && c.out === 0 && c.ready >= remaining,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, statusMap]);

  if (!r) return null;

  const ds = r.delivery_services || r.deliveryServices || {};
  const isPickup = !!ds?.pickup?.enabled && !ds?.delivery?.enabled;

  const headline = (() => {
    if (readiness.total === 0) return "No items yet";
    if (readiness.allDone) return "Order complete";
    if (readiness.allOut) return "Out for delivery";
    if (readiness.out > 0) return "Partly out for delivery";
    if (readiness.allReady) return isPickup ? "Ready for pickup" : "Ready for delivery";
    if (readiness.received > 0) return "Partly delivered";
    if (readiness.ready > 0) return "Partly ready";
    return "On order";
  })();

  const detail = (() => {
    const p = [];
    if (readiness.received > 0) p.push(`${readiness.received} delivered`);
    if (readiness.out > 0) p.push(`${readiness.out} out for delivery`);
    if (readiness.ready > 0) p.push(`${readiness.ready} at the store`);
    if (readiness.waiting > 0) p.push(`${readiness.waiting} on order`);
    return p.join(" · ");
  })();

  const rail = (() => {
    const bands = [
      ["received", "bg-emerald-500", readiness.received],
      ["out", "bg-sky-500", readiness.out],
      ["ready", "bg-teal-500", readiness.ready],
      ["waiting", "bg-slate-200", readiness.waiting],
    ].filter((b) => b[2] > 0);
    return { bands, discrete: readiness.total > 0 && readiness.total <= 14 };
  })();

  const pay = getPaymentStatus(r);
  const amountPaid = parseFloat(r.amount_paid ?? r.amountPaid) || 0;
  const customerName = r.customer_name || r.customerName || "Customer";
  const phone = r.customer_phone || r.customerPhone || "";
  const address = r.delivery_address || r.deliveryAddress || "";
  const apt = r.apartment_number || r.apartmentNumber || "";
  const deliveryDate = r.delivery_date || r.deliveryDate || "";
  const createdAt = r.created_at || r.createdAt;
  const sentAt = r.sent_at || r.sentAt;
  const optedOut = !!r.sms_opted_out;
  const smsConsent = !!(r.sms_consent ?? r.smsConsent);
  const aiHandling = r.is_ai_handling !== false && r.isAIHandling !== false;
  const messages = (r.messages || []).filter((m) => m.message_type !== "survey");
  const escalation = escalations?.[r.id];
  const paymentHistory = Array.isArray(r.payment_history || r.paymentHistory)
    ? r.payment_history || r.paymentHistory
    : [];
  const hasSignature = !!(r.delivery_signature || r.deliverySignature);
  const prefs = r.customerPreferences || r.customer_preferences || {};
  const prefDay = prefs.preferred_delivery_day || prefs.preferredDeliveryDay;
  const prefTime = prefs.preferred_time_window || prefs.preferredTimeWindow;
  const prefNotes = prefs.notes || r.customer_notes || "";
  const internalNotes = r.internal_notes || r.internalNotes || "";
  const taxRate = parseFloat(ds?.taxExempt ? 0 : storeSettings?.tax_rate ?? 8);

  const fees = [
    ["Delivery", ds?.delivery],
    ["Setup / assembly", ds?.setup],
    ["Haul away", ds?.wastePickup],
    ["Pickup", ds?.pickup],
  ].filter(([, f]) => f?.enabled && parseFloat(f?.price) > 0);

  const selectedIds = Object.keys(selectedItemsForDelivery || {}).filter((k) => selectedItemsForDelivery[k]);
  const selectedItems = items.filter((it) => selectedIds.includes(String(it.id)) || selectedIds.includes(it.id));

  const mfrName = (item) => {
    const id = item.manufacturerId || item.manufacturer_id;
    const m = id && (manufacturers || []).find((x) => x.id === id);
    return m ? m.name : null;
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    clearSelectedItemsForDelivery && clearSelectedItemsForDelivery();
  };

  // ---- status transitions: the same rules as the desktop pills ----
  const optionsFor = (item) => {
    const s = statusOf(item);
    const status = s.status;
    const isCompleted = ITEM_TERMINAL_STATUSES.includes(status);
    const isReadyish = status === "ready" || status === "partial";
    const isOut = status === "out_for_delivery";
    const name = item.name || item.item_name || "this item";
    const confirmReopen = (target) =>
      !isCompleted || window.confirm(`Move "${name}" back to ${target}? This reopens the order.`);

    const markReady = () => handleMarkItemReady(r.id, item);
    const markStatus = (st) => handleMarkUnitStatus(r.id, item, st);
    const undoReady = () => handleUndoReady(r.id, item);
    const undoDelivery = () => handleUndoDelivery(item, r);

    const opts = [];
    // Ready
    if (status === "waiting") opts.push({ key: "ready", run: markReady });
    else if (isOut) opts.push({ key: "ready", run: () => markStatus("at_store") });
    else if (isCompleted) opts.push({ key: "ready", run: async () => { if (confirmReopen("Ready")) await undoDelivery(); } });
    else opts.push({ key: "ready", current: true });
    // Out for delivery / Delivered / Picked up
    ["out_for_delivery", "delivered", "picked_up"].forEach((key) => {
      if (key === status) opts.push({ key, current: true });
      else if (!isCompleted) opts.push({ key, run: () => markStatus(key) });
      else opts.push({ key, run: async () => { if (!confirmReopen(STATUS_LABEL[key])) return; await undoDelivery(); await markStatus(key); } });
    });
    // Waiting (back)
    if (status === "waiting") opts.push({ key: "waiting", current: true });
    else if (isReadyish) opts.push({ key: "waiting", run: undoReady });
    else if (isOut) opts.push({ key: "waiting", run: async () => { await markStatus("at_store"); await undoReady(); } });
    else if (isCompleted) opts.push({ key: "waiting", run: async () => { if (!confirmReopen("Waiting")) return; await undoDelivery(); await undoReady(); } });
    return opts;
  };

  const rowBusy = (item) =>
    Boolean(itemStatusBusy?.[item.id]) || isUndoingDelivery === item.id || (markingItemReady && !ITEM_TERMINAL_STATUSES.includes(statusOf(item).status));

  // ---- bulk (same loops the old screen had, just reachable now) ----
  const bulk = async (kind) => {
    if (bulkBusy || selectedItems.length === 0) return;
    let targets = [];
    let label = "";
    if (kind === "ready") {
      targets = selectedItems.filter((it) => statusOf(it).status === "waiting");
      label = "Ready";
    } else {
      targets = selectedItems.filter((it) => ["ready", "partial", "out_for_delivery", "waiting"].includes(statusOf(it).status));
      label = STATUS_LABEL[kind];
    }
    if (targets.length === 0) return;
    if (!window.confirm(`Mark ${targets.length} item${targets.length === 1 ? "" : "s"} as ${label}?`)) return;
    setBulkBusy(true);
    try {
      for (const it of targets) {
        if (kind === "ready") await handleMarkItemReady(r.id, it);
        else await handleMarkUnitStatus(r.id, it, kind, { skipConfirm: true });
      }
    } finally {
      setBulkBusy(false);
      exitSelectMode();
    }
  };

  const smsBusy = sendingSMSReceiptId === r.id;
  const smsJustSent = !!smsSentReceipts?.[r.id];

  // Group items: set pieces sit under a small set label; everything else is flat.
  const displayGroups = (() => {
    const groups = [];
    const sets = {};
    items.forEach((item, i) => {
      const setId = item.set_id || item.setId;
      if (setId) {
        if (!sets[setId]) {
          sets[setId] = { type: "set", setId, setName: item.set_name || item.setName || "Set", items: [] };
          groups.push(sets[setId]);
        }
        sets[setId].items.push({ ...item, originalIndex: i });
      } else groups.push({ type: "single", item: { ...item, originalIndex: i } });
    });
    return groups;
  })();

  // Plain render function (not a nested component) so rows are not
  // remounted on every parent render.
  const renderItemRow = (item, nested = false) => {
    const s = statusOf(item);
    const status = s.status;
    const chipLabel = status === "partial" ? `${s.ready || 0}/${item.quantity || 1} ready` : STATUS_LABEL[status] || "Waiting";
    const busy = rowBusy(item);
    const selected = !!selectedItemsForDelivery?.[item.id];
    const Icon = STATUS_ICON[status] || Clock;
    return (
      <div key={item.id || item.originalIndex} className={`flex items-center gap-3 px-4 py-3 min-h-[60px] ${nested ? "pl-7" : ""}`}>
        {selectMode ? (
          <button
            type="button"
            role="checkbox"
            aria-checked={selected}
            onClick={() => toggleItemForDelivery(item.id)}
            className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${selected ? "bg-blue-600 border-blue-600" : "border-slate-300 bg-white"}`}
          >
            {selected && <Check className="w-4 h-4 text-white" />}
          </button>
        ) : (
          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_DOT[status] || STATUS_DOT.waiting}`} />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-medium text-slate-900 truncate">{item.name || item.item_name || "Item"}</p>
          <p className="text-[13px] text-slate-500 truncate">
            {[item.sku, mfrName(item), item.color, item.quantity > 1 ? `Qty ${item.quantity}` : null].filter(Boolean).join(" · ")}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <p className="text-[15px] text-slate-900 tabular-nums">{money(item.price)}</p>
          <button
            type="button"
            disabled={busy || selectMode}
            onClick={() => setStatusTarget(item)}
            className={`min-h-[32px] px-2.5 rounded-full text-[12px] font-semibold flex items-center gap-1.5 ${STATUS_CHIP[status] || STATUS_CHIP.waiting} disabled:opacity-60`}
          >
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
            <span>{chipLabel}</span>
            {!busy && !selectMode && <ChevronRight className="w-3 h-3 opacity-60 -mr-0.5" />}
          </button>
        </div>
      </div>
    );
  };

  // ------------------------------------------------------------
  // Chat screen (pushed on top of the receipt)
  // ------------------------------------------------------------
  const chatScreen = isMobileChatOpen && (
    <div className="sm:hidden fixed inset-0 bg-white z-[120] flex flex-col rdm-slide-in">
      <div className="flex-shrink-0 bg-white" style={{ height: "env(safe-area-inset-top)" }} />
      <div className="flex-shrink-0 border-b border-slate-200">
        <div className="h-12 px-2 flex items-center">
          <button
            type="button"
            onClick={() => setIsMobileChatOpen(false)}
            className="min-w-[44px] h-11 px-2 flex items-center gap-0.5 text-blue-600 -ml-1"
          >
            <ChevronLeft className="w-6 h-6" />
            <span className="text-[16px]">Receipt</span>
          </button>
          <div className="flex-1 min-w-0 text-center px-2">
            <p className="text-[16px] font-semibold text-slate-900 truncate">{customerName}</p>
          </div>
          {aiHandling ? (
            <button
              type="button"
              onClick={handleTakeOverChat}
              disabled={isTakingOver}
              className="h-9 px-3 rounded-full bg-slate-900 text-white text-[13px] font-semibold flex items-center gap-1.5 disabled:opacity-50"
            >
              {isTakingOver ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
              <span>{isTakingOver ? "Taking over" : "Take over"}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleReturnToAI}
              disabled={isReturningToAI}
              className="h-9 px-3 rounded-full bg-slate-100 text-slate-800 text-[13px] font-semibold flex items-center gap-1.5 disabled:opacity-50"
            >
              {isReturningToAI ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
              <span>{isReturningToAI ? "Returning" : "Hand to AI"}</span>
            </button>
          )}
        </div>
        <div className="px-4 pb-2 flex items-center justify-center gap-2 text-[12px] text-slate-500">
          <span className={`w-1.5 h-1.5 rounded-full ${isStaffMode ? "bg-emerald-500" : "bg-blue-500"}`} />
          {isStaffMode ? "You are replying" : "AI is replying"}
        </div>
        {optedOut && (
          <div className="mx-4 mb-3 px-3 py-2 rounded-xl bg-red-50 text-red-700 text-[13px] flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Customer opted out of texts. They must text START to re-enable.</span>
          </div>
        )}
      </div>

      <div ref={mobileChatContainerRef} onScroll={handleChatScroll} className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-slate-50">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            {isLoadingReceiptDetails ? (
              <>
                <RefreshCw className="w-6 h-6 text-slate-300 animate-spin mb-3" />
                <p className="text-slate-400 text-sm">Loading conversation</p>
              </>
            ) : (
              <>
                <MessageSquare className="w-10 h-10 text-slate-300 mb-3" />
                <p className="text-slate-500 text-sm">No messages yet</p>
                <p className="text-slate-400 text-xs mt-1">Messages appear when the customer texts</p>
              </>
            )}
          </div>
        ) : (
          messages.map((m, i) => {
            const mine = m.role !== "user";
            return (
              <div key={m.id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[82%]">
                  <div
                    className={`px-3.5 py-2.5 text-[15px] leading-snug ${
                      m.role === "user"
                        ? "bg-white text-slate-900 rounded-2xl rounded-bl-md border border-slate-200"
                        : m.role === "staff"
                          ? "bg-blue-600 text-white rounded-2xl rounded-br-md"
                          : "bg-slate-200 text-slate-900 rounded-2xl rounded-br-md"
                    }`}
                  >
                    {m.role === "assistant" && (
                      <p className="text-[11px] font-semibold text-slate-500 mb-0.5 flex items-center gap-1">
                        <Bot className="w-3 h-3" /> AI
                      </p>
                    )}
                    {m.role === "staff" && m.staffName && (
                      <p className="text-[11px] font-semibold text-blue-100 mb-0.5">{m.staffName}</p>
                    )}
                    {renderMessageContent(m.content, m.role === "user")}
                  </div>
                  <p className={`text-[11px] text-slate-400 mt-1 px-1 ${mine ? "text-right" : ""}`}>{formatMessageTime(m)}</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {optedOut ? (
        <div className="flex-shrink-0 px-4 py-3 border-t border-slate-200 bg-white text-center" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}>
          <p className="text-[14px] text-slate-500">Messaging is off until the customer texts START</p>
        </div>
      ) : isStaffMode ? (
        <div className="flex-shrink-0 px-3 pt-2 border-t border-slate-200 bg-white" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 8px)" }}>
          {staffImage && (
            <div className="mb-2 relative inline-block">
              <img src={URL.createObjectURL(staffImage)} alt="Attachment preview" className="max-h-24 rounded-lg border border-slate-200" />
              <button type="button" aria-label="Remove attached image" onClick={() => setStaffImage(null)} className="absolute -top-2 -right-2 w-6 h-6 bg-slate-900 text-white rounded-full flex items-center justify-center">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <label className="w-11 h-11 rounded-full flex items-center justify-center text-slate-500 active:bg-slate-100 cursor-pointer flex-shrink-0">
              <ImagePlus className="w-6 h-6" />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                      addNotification({ title: "Image too large", message: "Max 5MB", isEscalation: true });
                    } else setStaffImage(file);
                  }
                  e.target.value = "";
                }}
              />
            </label>
            <textarea
              value={staffMessage}
              onChange={(e) => setStaffMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendStaffMessage();
                }
              }}
              placeholder={staffImage ? "Add a caption" : "Message"}
              rows={1}
              className="flex-1 min-h-[44px] max-h-32 px-4 py-2.5 bg-slate-100 rounded-2xl text-[16px] text-slate-900 placeholder-slate-400 focus:outline-none resize-none"
            />
            <button
              type="button"
              aria-label="Send message"
              onClick={handleSendStaffMessage}
              disabled={(!staffMessage.trim() && !staffImage) || sendingMessage}
              className="w-11 h-11 rounded-full bg-blue-600 text-white flex items-center justify-center disabled:opacity-40 flex-shrink-0"
            >
              {sendingMessage ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-shrink-0 px-4 py-3 border-t border-slate-200 bg-white" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}>
          <button
            type="button"
            onClick={handleTakeOverChat}
            disabled={isTakingOver}
            className="w-full h-12 rounded-xl bg-slate-900 text-white text-[16px] font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isTakingOver ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Users className="w-5 h-5" />}
            <span>Take over from AI to reply</span>
          </button>
        </div>
      )}
    </div>
  );

  // ------------------------------------------------------------
  // Receipt screen
  // ------------------------------------------------------------
  const statusItem = statusTarget && items.find((it) => it.id === statusTarget.id);

  return (
    <div className="sm:hidden w-full h-full bg-slate-50 flex flex-col rdm-slide-in">
      <style>{`
        @keyframes rdmSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes rdmSheetIn { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .rdm-slide-in { animation: rdmSlideIn 260ms cubic-bezier(0.32, 0.72, 0, 1); }
        .rdm-sheet-in { animation: rdmSheetIn 220ms cubic-bezier(0.32, 0.72, 0, 1); }
      `}</style>

      {/* Status bar */}
      <div className="flex-shrink-0 bg-white" style={{ height: "env(safe-area-inset-top)" }} />

      {/* Nav bar */}
      <div className="flex-shrink-0 h-12 px-2 flex items-center bg-white border-b border-slate-200">
        <button
          type="button"
          onClick={selectMode ? exitSelectMode : handleAttemptCloseReceiptModal}
          className="min-w-[44px] h-11 px-2 flex items-center gap-0.5 text-blue-600 -ml-1"
        >
          <ChevronLeft className="w-6 h-6" />
          <span className="text-[16px]">{selectMode ? "Cancel" : "Orders"}</span>
        </button>
        <div className="flex-1 min-w-0 text-center px-2">
          <p className="text-[16px] font-semibold text-slate-900 truncate">
            {selectMode ? `${selectedItems.length} selected` : customerName}
          </p>
        </div>
        {selectMode ? (
          <span className="min-w-[44px]" />
        ) : (
          <button
            type="button"
            aria-label="More actions"
            onClick={() => setMenuOpen(true)}
            className="w-11 h-11 flex items-center justify-center text-blue-600 -mr-1"
          >
            <MoreHorizontal className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Scroll body */}
      <div className="flex-1 overflow-y-auto pb-6">
        {/* Online order banner (website orders, not floor sales) */}
        {isOnlineOrder(r) && (
          <div className="mx-4 mt-4 px-4 py-3 rounded-2xl bg-violet-600 text-white flex items-center gap-3">
            <Globe className="w-5 h-5 flex-shrink-0" />
            <p className="text-[15px] font-black uppercase tracking-wide truncate">{onlineOrderLabel(r)}</p>
          </div>
        )}

        {/* Needs-attention banner */}
        {escalation && !escalation.acknowledged && (
          <div className="mx-4 mt-4 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[14px] font-semibold text-amber-900">Needs attention</p>
              <p className="text-[13px] text-amber-800">{escalation.reason}</p>
            </div>
          </div>
        )}

        {/* Hero */}
        <div className="mx-4 mt-4 px-5 pt-5 pb-4 rounded-2xl bg-white border border-slate-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-[24px] leading-tight font-semibold tracking-[-0.02em] text-slate-900">{headline}</h2>
              {detail && <p className="text-[13px] text-slate-500 mt-1">{detail}</p>}
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-[20px] font-semibold text-slate-900 tabular-nums">{money(r.total)}</p>
              <p className={`text-[13px] font-medium ${pay.status === "paid" ? "text-emerald-600" : pay.status === "partial" ? "text-amber-600" : "text-red-600"}`}>
                {pay.status === "paid" ? "Paid" : pay.status === "partial" ? `${money(pay.balance)} due` : "Unpaid"}
              </p>
            </div>
          </div>

          {/* Piece rail: one bar, one job */}
          {readiness.total > 0 && (
            <div className="mt-4 flex gap-[3px] h-1.5" role="img" aria-label={detail || headline}>
              {rail.discrete
                ? rail.bands.flatMap(([k, cls, n]) => Array.from({ length: n }, (_, i) => <span key={`${k}-${i}`} className={`flex-1 rounded-full ${cls}`} />))
                : rail.bands.map(([k, cls, n]) => <span key={k} className={`rounded-full ${cls}`} style={{ flex: n }} />)}
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[13px] text-slate-500">
            <span>
              {isPickup ? "Pickup" : "Delivery"}
              {deliveryDate && fmtDay(deliveryDate) ? ` · ${fmtDay(deliveryDate)}` : " · date not set"}
            </span>
            <span className="tabular-nums">
              {createdAt ? new Date(createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
              {r.id ? ` · ${String(r.id).slice(0, 8).toUpperCase()}` : ""}
            </span>
          </div>
        </div>

        {/* Items */}
        <Group
          title={`Items${items.length ? ` · ${items.length}` : ""}`}
          trailing={
            items.length > 1 && !selectMode ? (
              <button type="button" onClick={() => setSelectMode(true)} className="min-h-[32px] px-2 -mr-2 text-[14px] font-medium text-blue-600">
                Select
              </button>
            ) : null
          }
        >
          {isLoadingReceiptDetails && items.length === 0 && (
            <>
              <div className="h-[60px] animate-pulse bg-slate-50" />
              <div className="h-[60px] animate-pulse bg-slate-50" />
            </>
          )}
          {!isLoadingReceiptDetails && items.length === 0 && (
            <p className="px-4 py-4 text-[14px] text-slate-500">No items on this receipt.</p>
          )}
          {displayGroups.map((g) =>
            g.type === "set" ? (
              <div key={`set-${g.setId}`}>
                <div className="px-4 pt-3 pb-1 flex items-center justify-between">
                  <p className="text-[13px] font-semibold text-slate-700">
                    {g.setName} <span className="font-normal text-slate-400">· {g.items.length} pieces</span>
                  </p>
                  <p className="text-[13px] text-slate-500 tabular-nums">{money(g.items.reduce((s, it) => s + (parseFloat(it.price) || 0), 0))}</p>
                </div>
                {g.items.map((it) => renderItemRow(it, true))}
              </div>
            ) : (
              renderItemRow(g.item)
            ),
          )}
        </Group>

        {/* Customer */}
        <Group title="Customer">
          {phone ? (
            <Row
              Icon={Phone}
              label={phone}
              sub={optedOut ? "Opted out of texts" : smsConsent ? "Texts allowed" : "No text consent"}
              value="Call"
              valueClass="text-blue-600 font-medium"
              onClick={() => (window.location.href = `tel:${digitsOnly(phone)}`)}
            />
          ) : (
            <Row Icon={Phone} label="No phone number" />
          )}
          <Row
            Icon={Send}
            label={sentAt ? "Receipt texted" : "Receipt not sent"}
            sub={sentAt ? new Date(sentAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "Send the customer their receipt link"}
            value={smsBusy ? "Sending" : smsJustSent ? "Sent" : sentAt ? "Resend" : "Send"}
            valueClass={smsJustSent ? "text-emerald-600 font-medium" : "text-blue-600 font-medium"}
            onClick={!smsBusy && phone ? () => handleSendSMS(r) : undefined}
          />
          {(r.customer_email || r.customerEmail) && <Row label="Email" value={r.customer_email || r.customerEmail} />}
          {r.salesperson && <Row label="Salesperson" value={r.salesperson} />}
        </Group>

        {/* Delivery */}
        <Group title={isPickup ? "Pickup" : "Delivery"}>
          <Row label="Expected" value={fmtDay(deliveryDate, { weekday: "short", month: "short", day: "numeric", year: "numeric" }) || "Not set"} />
          {address ? (
            <Row
              label="Address"
              sub={`${address}${apt && !address.toLowerCase().includes(`apt ${apt}`.toLowerCase()) ? `, Apt ${apt}` : ""}`}
              chevron
              onClick={() => window.open(`https://maps.apple.com/?q=${encodeURIComponent(address)}`, "_blank")}
            />
          ) : (
            !isPickup && <Row label="Address" value="Not set" />
          )}
          {(prefDay || prefTime) && <Row label="Preferred" value={[prefDay, prefTime].filter(Boolean).join(", ")} />}
          {prefNotes && <Row label="Instructions" sub={prefNotes} />}
          {hasSignature && (
            <Row Icon={PenLine} label="Delivery signature" sub="Customer confirmed delivery" chevron onClick={() => openSignaturesDrawer(r)} />
          )}
        </Group>

        {/* Payment */}
        <Group title="Payment">
          <Row label="Subtotal" value={money(r.subtotal)} />
          {fees.map(([label, f]) => (
            <Row key={label} label={label} value={money(f.price)} />
          ))}
          <Row label={`Tax${Number.isFinite(taxRate) ? ` (${taxRate}%)` : ""}`} value={money(r.tax)} />
          <Row label="Total" value={money(r.total)} valueClass="font-semibold text-slate-900" />
          <Row label="Paid" value={money(amountPaid)} />
          <Row
            label="Balance due"
            value={money(pay.balance)}
            valueClass={pay.status === "paid" ? "text-emerald-600 font-semibold" : "text-slate-900 font-semibold"}
          />
          {paymentHistory.length > 0 && (
            <div>
              <Row
                label={`Payment history · ${paymentHistory.length}`}
                chevron={!showAllPayments}
                onClick={() => setShowAllPayments((v) => !v)}
              />
              {showAllPayments &&
                paymentHistory.map((p, i) => (
                  <Row
                    key={p.id || i}
                    label={fmtDay(p.date, { month: "short", day: "numeric", year: "numeric" }) || "Payment"}
                    sub={[p.paymentType, p.note].filter(Boolean).join(" · ")}
                    value={money(p.amount)}
                  />
                ))}
            </div>
          )}
        </Group>

        {/* Notes */}
        {internalNotes && (
          <Group title="Store notes">
            <div className="px-4 py-3">
              <p className="text-[15px] text-slate-900 whitespace-pre-wrap">{internalNotes}</p>
              <p className="text-[12px] text-slate-400 mt-1">Used by the AI when answering this customer.</p>
            </div>
          </Group>
        )}

        {ITEM_TERMINAL_STATUSES.includes(r.delivery_status || r.deliveryStatus) && (
          <div className="mx-4 mt-6">
            <SurveyFeedbackDisplay receipt={r} />
          </div>
        )}
        <div className="h-24" />
      </div>

      {/* Bottom bar */}
      <div className="flex-shrink-0 px-4 pt-3 bg-white border-t border-slate-200" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}>
        {selectMode ? (
          <div className="flex gap-2">
            <button type="button" disabled={bulkBusy || selectedItems.length === 0} onClick={() => bulk("ready")} className="flex-1 h-12 rounded-xl bg-slate-100 text-slate-900 text-[15px] font-semibold disabled:opacity-40">
              Ready
            </button>
            <button type="button" disabled={bulkBusy || selectedItems.length === 0} onClick={() => bulk("delivered")} className="flex-1 h-12 rounded-xl bg-emerald-600 text-white text-[15px] font-semibold disabled:opacity-40">
              Delivered
            </button>
            <button type="button" disabled={bulkBusy || selectedItems.length === 0} onClick={() => bulk("picked_up")} className="flex-1 h-12 rounded-xl bg-emerald-600 text-white text-[15px] font-semibold disabled:opacity-40">
              Picked up
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsMobileChatOpen(true)}
            className="w-full h-12 rounded-xl bg-blue-600 text-white text-[16px] font-semibold flex items-center justify-center gap-2 active:bg-blue-700"
          >
            <MessageSquare className="w-5 h-5" />
            <span>Customer chat</span>
            {messages.length > 0 && (
              <span className="ml-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-white/20 text-[12px] font-semibold flex items-center justify-center tabular-nums">
                {messages.length}
              </span>
            )}
          </button>
        )}
      </div>

      {/* More menu */}
      <Sheet open={menuOpen} title={customerName} subtitle={r.id ? `Receipt ${String(r.id).slice(0, 8).toUpperCase()}` : null} onClose={() => setMenuOpen(false)}>
        <SheetButton
          label="Edit receipt"
          Icon={Edit2}
          onClick={() => {
            setMenuOpen(false);
            handleCloseReceiptModal();
            handleOpenModal(r);
          }}
        />
        <SheetButton label="Share receipt link" Icon={Share2} onClick={() => { setMenuOpen(false); handleShareReceipt(r); }} />
        <SheetButton label="Print" Icon={Printer} onClick={() => { setMenuOpen(false); handlePrintReceipt(r); }} />
        <SheetButton label="Delete receipt" Icon={Trash2} danger onClick={() => { setMenuOpen(false); handleDeleteReceipt(r.id); }} />
        <button type="button" onClick={() => setMenuOpen(false)} className="w-full h-12 mt-1 rounded-xl bg-slate-100 text-[16px] font-semibold text-slate-900">
          Cancel
        </button>
      </Sheet>

      {/* Item status sheet */}
      <Sheet
        open={!!statusItem}
        title={statusItem ? statusItem.name || statusItem.item_name : ""}
        subtitle={statusItem ? [statusItem.sku, statusItem.quantity > 1 ? `Qty ${statusItem.quantity}` : null].filter(Boolean).join(" · ") || null : null}
        onClose={() => setStatusTarget(null)}
      >
        {statusItem &&
          optionsFor(statusItem).map((o) => (
            <SheetButton
              key={o.key}
              label={STATUS_LABEL[o.key]}
              Icon={STATUS_ICON[o.key]}
              current={o.current}
              disabled={o.current || !o.run}
              busy={rowBusy(statusItem)}
              onClick={async () => {
                if (!o.run) return;
                setStatusTarget(null);
                await o.run();
              }}
            />
          ))}
        <button type="button" onClick={() => setStatusTarget(null)} className="w-full h-12 mt-1 rounded-xl bg-slate-100 text-[16px] font-semibold text-slate-900">
          Cancel
        </button>
      </Sheet>

      {chatScreen}
    </div>
  );
};

export default ReceiptDetailMobile;
